import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { changeLanguage } from '@/i18n';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/authStore';
import { toast } from '@/hooks/use-toast';

interface LanguageSwitcherProps {
  className?: string;
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className,
  variant = 'ghost',
  size = 'icon'
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const { user } = useAuth();

  const handleLanguageChange = async (lng: 'ar' | 'fr') => {
    try {
      // Update language locally and in backend if user is authenticated
      await changeLanguage(lng, !!user);

      if (user) {
        toast({
          title: lng === 'ar' ? 'تم تغيير اللغة' : 'Langue changée',
          description: lng === 'ar'
            ? 'تم حفظ تفضيلات اللغة بنجاح'
            : 'Préférences de langue enregistrées avec succès',
        });
      }
    } catch (error) {
      console.error('Failed to change language:', error);
      toast({
        title: lng === 'ar' ? 'خطأ' : 'Erreur',
        description: lng === 'ar'
          ? 'فشل في تغيير اللغة'
          : 'Échec du changement de langue',
        variant: 'destructive',
      });
    }
  };

  const languages = [
    { code: 'ar', name: 'العربية', short: 'ع' },
    { code: 'fr', name: 'Français', short: 'FR', badge: 'Beta' }
  ];

  // Get current direction from document
  const isRTL = i18n.language === 'ar';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn(
            "transition-all duration-200 rounded-full",
            "hover:bg-muted hover:scale-110",
            "text-muted-foreground hover:text-foreground",
            size === 'icon' && "h-9 w-9",
            className
          )}
          title="تغيير اللغة / Changer de langue"
        >
          <Globe className="w-4 h-4" />
          {size !== 'icon' && (
            <span className="ms-2 font-medium">
              {currentLanguage === 'ar' ? 'العربية' : 'Français'}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={isRTL ? 'start' : 'end'}
        className="min-w-[180px] p-1"
        sideOffset={5}
      >
        {languages.map((lang) => {
          const isActive = currentLanguage === lang.code;
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code as 'ar' | 'fr')}
              className={cn(
                "flex items-center justify-between gap-3 cursor-pointer rounded-md px-3 py-2.5 transition-colors",
                "hover:bg-accent/80 focus:bg-accent/80",
                isActive && "bg-accent/60"
              )}
            >
              <div className="flex items-center gap-2.5">
                <div className={cn(
                  "flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors",
                  isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                )}>
                  {lang.short}
                </div>
                <div className="flex flex-col">
                  <span className={cn(
                    "text-sm font-medium leading-none",
                    isActive && "text-primary"
                  )}>
                    {lang.name}
                  </span>
                  {lang.badge && (
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {lang.badge}
                    </span>
                  )}
                </div>
              </div>
              {isActive && (
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10">
                  <span className="text-primary text-sm font-bold">✓</span>
                </div>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
